/*  ========================================================================
    # Sections - Product Slider
    ========================================================================  */
import { MotionConfig, motion, useMotionValue, useReducedMotion } from 'motion/react';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import ProductCard from '../../components/ProductCard';
import useTranslation from '../../hooks/useTranslation';
import useSVGSprite from '../../hooks/useSVGSprite';
import useResize from '../../hooks/useResize';
import useBreakepoints from '../../hooks/useBreakpoints';

const ProductSlider = ({ hideProductImages, products }) => {
  const sprite = useSVGSprite();
  const __ = useTranslation();
  const { xl, lg } = useBreakepoints();
  const sliderRef = useRef(null);
  const slidesRef = useRef([]);
  const dragX = useMotionValue(0);
  const reduced = useReducedMotion();
  const [scrollable, setScrollable] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [itemDisplayCount, setItemDisplayCount] = useState(0);

  const canScrollLeft = activeSlide > 0;
  const canScrollRight = activeSlide + itemDisplayCount < products.length;

  const navigateLeft = () => {
    if (canScrollLeft) {
      setActiveSlide(activeSlide - 1);
    }
  };

  const navigateRight = () => {
    if (canScrollRight) {
      setActiveSlide(activeSlide + 1);
    }
  };

  const onResize = useCallback(() => {
    if (sliderRef.current.scrollWidth > sliderRef.current.clientWidth) {
      setScrollable(true);
    } else {
      setScrollable(false);
    }

    if (xl) {
      setItemDisplayCount(3);
    } else if (lg) {
      setItemDisplayCount(2);
    } else {
      setItemDisplayCount(1);
    }
  }, [sliderRef, xl, lg]);

  // Register resize event listeners to update dimensions
  useResize(sliderRef, onResize);

  // Handle item key down
  const handleItemKeyDown = (e, index) => {
    if (e.key === 'ArrowLeft') {
      const item = slidesRef.current[index - 1];
      if (item) {
        item.focus();
      }
    } else if (e.key === 'ArrowRight') {
      const item = slidesRef.current[index + 1];
      if (item) {
        item.focus();
      }
    }
  };

  // Handle item focus
  const handleItemFocus = (e, index) => {
    if (index + 1 > itemDisplayCount) {
      navigateRight();
    } else if (index < activeSlide) {
      navigateLeft();
    }
  };

  return (
    <MotionConfig reducedMotion='user'>
      <div className='s-product-overview__wrapper'>
        {scrollable && (
          <div
            className='s-product-overview__controls'
            role='navigation'
          >
            <button
              aria-label={__('slider-control-left')}
              onClick={navigateLeft}
              disabled={!canScrollLeft}
            >
              <svg viewBox='0 0 20 20' aria-hidden>
                <use href={`${sprite}#angle-left`} />
              </svg>
            </button>
            <button
              aria-label={__('slider-control-right')}
              onClick={navigateRight}
              disabled={!canScrollRight}
            >
              <svg viewBox='0 0 20 20' aria-hidden>
                <use href={`${sprite}#angle-right`} />
              </svg>
            </button>
          </div>
        )}
        <motion.div
          ref={sliderRef}
          className='s-product-overview__slider row'
          style={{ justifyContent: !scrollable && products.length <= 2 ? 'center' : null, x: dragX }}
          role='slider'
          drag={reduced ? false : 'x'}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={{ left: 0.35, right: 0.35 }}
          onDragEnd={() => {
            const x = dragX.get();
            if (x <= -15) navigateRight();
            if (x >= 15) navigateLeft();
          }}
        >
          {products.map((entry, index) => (
            <motion.div
              key={index}
              className='s-product-overview__item'
              initial={{ x: 0, y: 50, opacity: 0 }}
              animate={{ x: -activeSlide * 100 + '%', y: 0, opacity: 1 }}
              transition={{ delay: 0.012 * index, ease: 'easeOut' }}
              tabIndex={0}
              ref={entry => { slidesRef.current[index] = entry; }}
              onFocus={e => handleItemFocus(e, index)}
              onKeyDown={e => handleItemKeyDown(e, index)}
            >
              <ProductCard
                hideImage={hideProductImages}
                upsellingContent={entry['showUpselling'] ? entry['upsellingContent'] : null}
                product={entry.product}
                selectItemObject={entry.selectItemObject}
                upsellingBadge={entry['showUpsellingBadge'] ? entry['upsellingBadgeText'] : null}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </MotionConfig>
  );
};

ProductSlider.propTypes = {
  hideProductImages: PropTypes.bool,
  products: PropTypes.array
};

export default ProductSlider;
